import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

import { config } from "../config";

interface SeoProps {
  title?: string;
  description?: string;
}

export function Seo({ title, description }: SeoProps) {
  const { t, i18n } = useTranslation("common");
  const lang = (i18n.resolvedLanguage?.slice(0, 2) || "fr") as "fr" | "en";
  const defaultTitle = `${config.brandName} — ${t("seo.titleSuffix")}`;
  const defaultDescription = t("seo.description", { brand: config.brandName });

  const pageTitle = title ?? defaultTitle;
  const pageDescription = description ?? defaultDescription;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: config.brandName,
    description: pageDescription,
    email: config.contactEmail,
    telephone: config.contactPhone,
    url: config.siteUrl,
    image: config.siteUrl + "/og-image.jpg",
    sameAs: [config.instagramUrl, config.facebookUrl].filter(Boolean),
    areaServed: "Senegal",
    priceRange: "$$",
  };

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={config.siteUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <link rel="alternate" hrefLang="fr" href={config.siteUrl} />
      <link rel="alternate" hrefLang="en" href={`${config.siteUrl}?lng=en`} />
      <link rel="alternate" hrefLang="x-default" href={config.siteUrl} />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
}
